import { useForm, type SubmitHandler } from 'react-hook-form'
import { useCallback, useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { createClient } from '@/util/supabase/component'
import { Spinner } from '.'
import { FormFieldWithErrors } from './FormFieldWithErrors'
import { CopyIcon } from 'lucide-react'

type FormData = { input: string }

const ReferenceId = ({ id }: { id: number }) => {
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (copied) {
      const timeout = setTimeout(() => setCopied(false), 1000)
      return () => clearTimeout(timeout)
    }
  }, [copied])

  const handleCopy = useCallback(async () => {
    await navigator.clipboard.writeText(id.toString())
    setCopied(true)
  }, [id])

  return (
    <button
      onClick={handleCopy}
      disabled={copied}
      title="Click to copy"
      className={`${
        copied ? 'bg-green-300' : 'bg-gray-100'
      } rounded-md px-2 py-1 text-xs flex align-middle items-center gap-1`}
    >
      {copied ? (
        'Copied!'
      ) : (
        <>
          {id} <CopyIcon size={16} className="text-gray-500" />
        </>
      )}
    </button>
  )
}

export const ReferenceSearch = () => {
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<FormData>()
  const [input, setInput] = useState<string>('')

  const supabase = createClient()
  const { data, isLoading, error } = useQuery({
    queryKey: ['searchReferences', input],
    queryFn: async (context) => {
      const searchTerms = context.queryKey[1].split(' ')
      const { data, error } = await supabase
        .from('references')
        .select()
        .textSearch('fts_doc_en', `'${searchTerms.join("' & '")}'`)
      if (error) throw new Error(error.message)
      return data
    },
    enabled: Boolean(input) && input.length > 0,
  })

  useEffect(() => {
    if (error) setError('input', { type: 'validate', message: error.message })
  }, [error, setError])

  const onSubmit: SubmitHandler<FormData> = useCallback(
    (data) => setInput(data.input),
    [setInput],
  )

  return (
    <div className="flex flex-col gap-4 w-[464px]">
      <form>
        <FormFieldWithErrors
          label="Search query"
          registerProps={register('input', { required: true })}
          autoComplete="off"
          required
          error={errors.input}
        />
        <div className="flex w-full justify-end">
          <button
            type="submit"
            className="rounded border-2 border-serblue-light bg-white py-1 px-1 text-sm"
            onClick={handleSubmit(onSubmit)}
            disabled={isLoading}
          >
            Search
          </button>
        </div>
      </form>
      {isLoading && <Spinner />}
      {data && data.length === 0 && <div>No results found</div>}
      {data && data.length > 0 && (
        <div className="mt-4 flex flex-col gap-2">
          {data.map((item) => (
            <div key={item.id} className="flex flex-row gap-2 text-sm">
              <div className="w-2/3">{item.description}</div>
              <div className="w-1/3">
                <ReferenceId id={item.id} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
